import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  copiarCampoGrid,
  eliminarFilaGrid,
  estadoInicial,
  resetModal,
  setModal
} from 'slices/modalSlice'
import { formatearFecha, validarRangoFechas } from '../../helpers/dateValidation.js'
import { toastErrorLog } from '../../helpers/toastErrorLog.js'
import { useAuth } from '../../hooks/useAuth'
import { usePaginacion } from '../hooks/usePaginacion'
import {
  actualizarLista,
  buscarListaPorEmpresa,
  eliminarLista,
  insertarLista
} from '../servicios/listaEnvioService.js'
import { ItemLista } from './ItemLista'
import styles from './Lista.module.css'
import { ListaContenido } from './ListaContenido'
import { useListaFiltros } from './useListaFiltros'

const campos = [
  {
    type: 'text',
    name: 'nombreLista',
    placeholder: 'Ingresá el nombre la lista',
    label: 'Nombre de la lista',
    maxLength: 30,
    isFullWidth: true,
    isEditable: true,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaDesde',
    label: 'Comienzo de la vigencia',
    isFullWidth: false,
    isEditable: false,
    isGrid: false
  },
  {
    type: 'date',
    name: 'vigenciaHasta',
    label: 'Fin de la vigencia',
    isFullWidth: false,
    isEditable: true,
    isGrid: false
  },
  {
    type: 'text',
    name: 'zona',
    placeholder: 'Zona del envío',
    label: 'Zona',
    maxLength: 40,
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'cantidadDesde',
    label: 'Cantidad desde',
    inputMode: 'numeric',
    step: '1',
    min: '0',
    max: '999999',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'cantidadHasta',
    label: 'Cantidad hasta',
    inputMode: 'numeric',
    step: '1',
    min: '0',
    max: '999999',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'costo',
    label: 'Costo (sin IVA)',
    inputMode: 'decimal',
    step: '0.01',
    min: '0',
    max: '99999999.99',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'number',
    name: 'tEstimado',
    label: 'Días de entrega',
    inputMode: 'numeric',
    step: '1',
    min: '0',
    max: '999',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: false
  },
  {
    type: 'select',
    name: 'moneda',
    placeholder: 'Seleccioná una',
    label: 'Moneda',
    isFullWidth: false,
    isEditable: true,
    isGrid: true,
    copy: true,
    options: [
      { value: 'ARS', label: 'ARS ($)', key: 1 },
      { value: 'USD', label: 'USD (U$S)', key: 2 },
      { value: 'EUR', label: 'EUR (€)', key: 3 }
    ]
  }
]

const PAGINA_ITEMS = 15

const Lista = () => {
  const [lista, setLista] = useState([])
  const topRef = useRef(null)
  const [listaAEliminar, setListaAEliminar] = useState(null)
  const [actualizar, setActualizar] = useState(false)
  const prevIdUsuarioRef = useRef(null)

  const dispatch = useDispatch()
  const { loginDataContext: { idUsuario, empid } = {}, esPerfilAdmin } = useAuth()
  const { editMode, values, errores, type } = useSelector((state) => state.genericModal)

  const {
    listaFiltradaOrdenada,
    busqueda,
    setBusqueda,
    handleBusqueda,
    handleOrdenarAZ,
    handleOrdenarPorVencimiento
  } = useListaFiltros(lista, 'nombreLista')

  const {
    itemsActual: listaActual,
    paginaActual,
    setPaginaActual,
    itemsPorPagina,
    setItemsPorPagina,
    totalPaginas
  } = usePaginacion(listaFiltradaOrdenada, PAGINA_ITEMS)

  const idVendedor = idUsuario || 0

  useEffect(() => {
    dispatch(setModal({ campos: campos }))
  }, [])

  useEffect(() => {
    if (!prevIdUsuarioRef.current && idUsuario) {
      prevIdUsuarioRef.current = idUsuario
      dispatch(setModal({ values: { idVendedor: idUsuario } }))
    } else if (prevIdUsuarioRef.current && prevIdUsuarioRef.current !== idUsuario) {
      dispatch(resetModal())
      dispatch(
        setModal({
          campos: campos,
          values: { idVendedor: idUsuario }
        })
      )
      prevIdUsuarioRef.current = idUsuario
      setLista([])
      setActualizar((prev) => !prev)
    }
  }, [idUsuario, dispatch])

  const handleChange = (e) => {
    const { name, value, filaIndex = '' } = e.target
    const campo = filaIndex !== '' ? `${name}_${filaIndex}` : name
    const nuevasValidaciones = { ...errores }
    const valorFinal = value

    if (filaIndex === '') {
      dispatch(setModal({ values: { ...values, [name]: valorFinal } }))
    } else {
      const copia = [...values.grilla]
      copia[filaIndex] = { ...copia[filaIndex], [name]: valorFinal }
      dispatch(setModal({ values: { grilla: copia } }))
    }

    if (errores[campo]) {
      delete nuevasValidaciones[campo]
    }
    dispatch(setModal({ errores: nuevasValidaciones }))
  }

  const editarLista = (item) => {
    const esSoloLectura = !(esPerfilAdmin || idUsuario === item.idVendedor)
    const filas = (item.grilla || [])
      .sort((a, b) => a.zona.toLowerCase().localeCompare(b.zona.toLowerCase()))
      .map((fila, idx) => ({
        zona: fila.zona ?? '',
        cantidadDesde: Number(fila.cantidadDesde ?? 0),
        cantidadHasta: Number(fila.cantidadHasta ?? 0),
        costo: Number(fila.costo ?? 0),
        moneda: fila.moneda ?? '',
        tEstimado: Number(fila.tEstimado ?? 0),
        posicion: fila.posicion ?? idx + 1
      }))

    dispatch(
      setModal({
        size: 'lg',
        title: esSoloLectura ? 'Ver lista' : 'Editar lista',
        type: 'form',
        body: '',
        show: true,
        editMode: !esSoloLectura,
        values: {
          idLista: item.idLista,
          idVendedor: item.idVendedor,
          nombreLista: item.nombreLista,
          nombreListaOriginal: item.nombreLista,
          vigenciaDesde: formatearFecha(item.vigenciaDesde, 'YYYY-MM-DD'),
          vigenciaHasta: formatearFecha(item.vigenciaHasta, 'YYYY-MM-DD'),
          vigenciaHastaOriginal: formatearFecha(item.vigenciaHasta, 'YYYY-MM-DD'),
          zona: item.zona,
          grilla: filas.length ? filas : estadoInicial.grilla,
          deshabilitado: esSoloLectura
        }
      })
    )
  }

  const eliminarListaPagina = (item) => {
    dispatch(
      setModal({
        size: 'sm',
        title: 'Eliminar lista',
        type: 'botones',
        body: `¿Estás seguro que deseas eliminar todas las filas de la lista "${item.nombreLista}"?`,
        show: true
      })
    )

    setListaAEliminar({
      idLista: item.idLista,
      idVendedor: item.idVendedor
    })
  }

  const abrirModal = () => {
    const fechaHoy = formatearFecha(new Date(), 'YYYY-MM-DD')

    dispatch(
      setModal({
        size: 'lg',
        title: 'Crear lista',
        type: 'form',
        body: '',
        show: true,
        editMode: false,
        values: {
          ...values,
          idVendedor: idVendedor,
          vigenciaDesde: fechaHoy,
          vigenciaHasta: ''
        }
      })
    )
  }

  const handleConfirmEliminar = async () => {
    if (!listaAEliminar) {
      return
    }
    try {
      await eliminarLista(listaAEliminar.idLista, listaAEliminar.idVendedor)
      setListaAEliminar(null)
      setActualizar((prev) => !prev)
      return true
    } catch (error) {
      const mensaje = `Ocurrió un error al eliminar la lista.`
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  const handleConfirmModal = async () => {
    if (values?.deshabilitado) {
      dispatch(setModal({ show: false }))
      return false
    }

    const errores = {}

    const { nombreLista, vigenciaDesde, vigenciaHasta } = values

    if ((!nombreLista || nombreLista.trim() === '') && !editMode) {
      errores.nombreLista = 'El nombre es obligatorio.'
    }

    if (!vigenciaDesde) {
      errores.vigenciaDesde = 'Debe seleccionar una fecha de comienzo de vigencia.'
    } else if (!editMode) {
      const esFechaDesdeValida = validarRangoFechas(new Date(), vigenciaDesde, 'day')
      if (!esFechaDesdeValida) {
        errores.vigenciaDesde = 'La fecha de inicio debe ser igual o posterior a la fecha actual.'
      }
    }

    if (!vigenciaHasta) {
      errores.vigenciaHasta = 'La fecha de fin de vigencia es obligatoria.'
    } else {
      const esFechaHastaNoPasada = validarRangoFechas(new Date(), vigenciaHasta, 'day')
      if (!esFechaHastaNoPasada) {
        errores.vigenciaHasta = 'La fecha de fin de vigencia no puede estar en el pasado.'
      }

      const esFechaHastaValida = validarRangoFechas(vigenciaDesde, vigenciaHasta, 'day')
      if (!errores.vigenciaHasta && !esFechaHastaValida) {
        errores.vigenciaHasta = 'La fecha de fin de vigencia debe ser mayor que la de comienzo.'
      }
    }

    const esVigenciaOriginalValida = validarRangoFechas(
      values.vigenciaHastaOriginal,
      vigenciaHasta,
      'day'
    )

    if (!errores.vigenciaHasta && editMode && !esVigenciaOriginalValida) {
      errores.vigenciaHasta = 'No puede establecer una fecha anterior a la vigencia original.'
    }

    values.grilla.forEach((fila, i) => {
      const { cantidadDesde, cantidadHasta, costo, moneda, tEstimado, zona } = fila

      if (!zona || zona.trim() === '') {
        errores[`zona_${i}`] = 'La zona es obligatoria.'
      }

      if (cantidadDesde < 0 || !cantidadDesde) {
        errores[`cantidadDesde_${i}`] = 'Debe ingresar una cantidad mínima.'
      }

      if (i > 0) {
        const zonaActual = fila.zona?.trim().toUpperCase()
        const desdeActual = Number(cantidadDesde)

        const filasMismaZona = values.grilla
          .filter((f, idx) => idx < i && f.zona?.trim().toUpperCase() === zonaActual)
          .sort((a, b) => Number(a.cantidadDesde) - Number(b.cantidadDesde))

        if (filasMismaZona.length > 0) {
          const filaAnterior = filasMismaZona[filasMismaZona.length - 1]
          const hastaAnterior = Number(filaAnterior.cantidadHasta)

          if (desdeActual !== hastaAnterior + 1) {
            errores[`cantidadDesde_${i}`] =
              `En la zona "${fila.zona}", la cantidad desde debe ser ${hastaAnterior + 1} para evitar brechas.`
          }
        }
      }

      const desde = Number(cantidadDesde)
      const hasta = Number(cantidadHasta)

      if (!hasta || hasta <= desde) {
        errores[`cantidadHasta_${i}`] = 'La cantidad máxima debe ser mayor que la mínima.'
      }

      const costoNumero = Number(costo)
      if (!costo || Number.isNaN(costoNumero) || costoNumero <= 0) {
        errores[`costo_${i}`] = 'Debe ingresar el valor del costo.'
      }

      if (!moneda) {
        errores[`moneda_${i}`] = 'Debe seleccionar una moneda.'
      }

      if (tEstimado <= 0 || !tEstimado) {
        errores[`tEstimado_${i}`] = 'Debe ingresar el tiempo estimado.'
      }
    })

    if (
      lista.some(
        (l) =>
          l.nombreLista.trim().toLowerCase() === values.nombreLista.trim().toLowerCase() &&
          (!editMode ||
            l.nombreLista.trim().toLowerCase() !== values.nombreListaOriginal?.trim().toLowerCase())
      )
    ) {
      errores.nombreLista = 'Ya existe una lista con este nombre.'
    }

    dispatch(
      setModal({
        errores: errores,
        type: 'form'
      })
    )

    if (Object.keys(errores).length > 0) {
      return false
    }
    try {
      if (editMode) {
        await handleActualizar()
      } else {
        await handleConfirmarAgregado()
      }
      return true
    } catch {
      return false
    }
  }

  const handleActualizar = async () => {
    try {
      const listasAEnviar = values.grilla.map((fila, index) => ({
        nombreLista: values.nombreLista,
        nombreListaOriginal: values.nombreListaOriginal,
        vigenciaDesde: values.vigenciaDesde,
        vigenciaHasta: values.vigenciaHasta,
        idVendedor: values.idVendedor,
        grilla: [
          {
            zona: fila.zona,
            cantidadDesde: Number(fila.cantidadDesde),
            cantidadHasta: Number(fila.cantidadHasta),
            costo: Number(fila.costo ?? 0),
            moneda: fila.moneda,
            tEstimado: Number(fila.tEstimado),
            posicion: index + 1
          }
        ]
      }))

      await actualizarLista(listasAEnviar)
      dispatch(setModal({ show: false }))
      setActualizar((prev) => !prev)
      toast.success('Lista actualizada correctamente.')
    } catch (error) {
      const mensaje = `Ocurrió un error al actualizar la lista.`
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  const handleConfirmarAgregado = async () => {
    const nuevaLista = {
      nombreLista: values.nombreLista,
      vigenciaDesde: values.vigenciaDesde,
      vigenciaHasta: values.vigenciaHasta,
      idVendedor: values.idVendedor,
      grilla: []
    }

    values.grilla.forEach((fila) => {
      nuevaLista.grilla.push({
        zona: fila.zona,
        cantidadDesde: fila.cantidadDesde,
        cantidadHasta: fila.cantidadHasta,
        costo: Number(fila.costo ?? 0),
        moneda: fila.moneda,
        tEstimado: fila.tEstimado
      })
    })
    try {
      await insertarLista(nuevaLista)
      setActualizar((prev) => !prev)
      dispatch(setModal({ show: false }))
      toast.success('Lista creada correctamente.')
    } catch (error) {
      const mensaje = `Ocurrió un error al agregar la lista.`
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  const traerInfo = async () => {
    try {
      if (!idVendedor) {
        return
      }
      const { data } = await buscarListaPorEmpresa(empid)

      const unificadas = Object.values(
        data.reduce((acc, item) => {
          const nombreNormalizado = item.nombreLista.trim().toLowerCase()
          const key = esPerfilAdmin
            ? `${nombreNormalizado}__${item.idVendedor ?? 'sin-vendedor'}`
            : nombreNormalizado

          if (!acc[key]) {
            acc[key] = {
              idLista: item.idLista,
              nombreLista: item.nombreLista,
              vigenciaDesde: item.vigenciaDesde,
              vigenciaHasta: item.vigenciaHasta,
              idVendedor: item.idVendedor,
              nombreVendedor: item.nombreVendedor,
              grilla: []
            }
          } else {
            acc[key].idListas = [...(acc[key].idListas || []), item.idLista]
          }

          if (Array.isArray(item.grilla)) {
            acc[key].grilla.push(...item.grilla)
          }

          return acc
        }, {})
      ).map((l) => ({
        ...l,
        idListas: l.idListas || [l.idLista],
        contador: l.grilla.length
      }))
      setLista(unificadas)
    } catch (error) {
      const mensaje = `Ocurrió un error al cargar las listas.`
      toastErrorLog(error, mensaje, 'listaEnvio')
    }
  }

  useEffect(() => {
    traerInfo()
  }, [actualizar, idVendedor])

  const agregarFila = () => {
    const nuevaFila = {
      zona: '',
      cantidadDesde: 0,
      cantidadHasta: 0,
      costo: 0,
      moneda: '',
      tEstimado: 0
    }

    dispatch(
      setModal({
        values: {
          grilla: [...(values.grilla || []), nuevaFila]
        }
      })
    )
  }

  const eliminarFila = (index) => {
    dispatch(eliminarFilaGrid(index))
  }

  const copiarCampo = (name, value) => {
    dispatch(copiarCampoGrid({ name, value }))
  }

  return (
    <ListaContenido
      containerClassName={styles.listaContainer}
      topRef={topRef}
      titulo="Listas de Envío"
      busqueda={busqueda}
      setBusqueda={setBusqueda}
      onBusqueda={(valor) => {
        handleBusqueda(valor)
        setPaginaActual(1)
      }}
      onOrdenarAZ={(dir) => {
        handleOrdenarAZ(dir)
        setPaginaActual(1)
      }}
      onOrdenarPorVencimiento={() => {
        handleOrdenarPorVencimiento()
        setPaginaActual(1)
      }}
      onAgregarLista={abrirModal}
      labelAgregar="Agregar Lista"
      items={listaActual}
      renderItem={(item, index) => (
        <div className="col-12 col-md-4 mb-3" key={item.idLista ?? index}>
          <ItemLista
            lista={item}
            onEditar={() => editarLista(item)}
            onEliminar={() => eliminarListaPagina(item)}
            muestraNombreVendedor={esPerfilAdmin && item.idVendedor !== idVendedor}
            esPerfilAdmin={esPerfilAdmin}
            idUsuario={idUsuario}
          />
        </div>
      )}
      onConfirmModal={type === 'form' ? handleConfirmModal : handleConfirmEliminar}
      onChangeModal={handleChange}
      modalGridProps={{
        onAddItem: agregarFila,
        onRemoveItem: eliminarFila,
        onCopyField: copiarCampo
      }}
      totalPaginas={totalPaginas}
      paginaActual={paginaActual}
      onCambioPagina={(num) => {
        setPaginaActual(num)
        topRef.current?.scrollIntoView({ behavior: 'smooth' })
      }}
      itemsPorPagina={itemsPorPagina}
      onCambioItemsPorPagina={(num) => {
        setItemsPorPagina(num)
        setPaginaActual(1)
        topRef.current?.scrollIntoView({ behavior: 'smooth' })
      }}
    />
  )
}

export default Lista
